// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { TableCell, IconButton, useTheme, alpha } from '@mui/material';
import { ReactComponent as AddIcon } from '../../../../assets/Icons/lucideIcons/plus.svg';
import PropTypes from 'prop-types';

import { useTableModification } from '../../hooks/useTableModification';
import { useTableState } from '../../context/TableSchemaBuilderContext';
import ResizeHandler from '../ResizeHandler';

// Set default and minimum widths
const DEFAULT_WIDTH = 100;
const MIN_WIDTH = 50;

const ColumnHeader = ({ index, columnName }) => {
  const theme = useTheme();
  const [showAddButton, setShowAddButton] = useState(false);

  const { schema, config } = useTableState();
  const { handleAddColumn, handleColumnResize } = useTableModification();
  const width = schema.columnSettings[columnName]?.width || DEFAULT_WIDTH;
  const readOnly = config?.readOnly || false;
  const editMode = config?.editMode;

  // Extract handler functions with useCallback
  const handleMouseEnter = useCallback(() => {
    // Show the add button only if not in read-only mode
    if (editMode || readOnly || config?.fromConstAttributeFeature) {
      return;
    }
    setShowAddButton(true);
  }, [editMode, readOnly, config?.fromConstAttributeFeature]);

  const handleMouseLeave = useCallback(() => {
    // Show the add button only if not in read-only mode
    if (editMode || readOnly || config?.fromConstAttributeFeature) {
      return;
    }
    setShowAddButton(false);
  }, [editMode, readOnly, config?.fromConstAttributeFeature]);

  const handleResizeColumn = useCallback(
    (newWidth) => {
      handleColumnResize(columnName, newWidth);
    },
    [columnName, handleColumnResize]
  );

  const handleAddColumnClick = useCallback(() => {
    handleAddColumn(index);
  }, [handleAddColumn, index]);

  return (
    <TableCell
      sx={{
        width,
        backgroundColor: alpha(theme?.palette?.primary?.main, 0.05),
        position: 'sticky',
        top: 0,
        zIndex: 5,
        minWidth: MIN_WIDTH,
        borderRight: `1px solid ${alpha(theme?.palette?.primary?.main, 0.2)}`,
        borderBottom: `1px solid ${alpha(theme?.palette?.primary?.main, 0.2)}`,
        padding: 0,
        '&:hover': {
          '& .column-add-button': {
            opacity: 1,
          },
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          color: theme?.palette?.text?.secondary,
          userSelect: 'none',
        }}
      >
        {columnName}
      </div>
      {
        // Only show the resize handler if not in read-only mode
        !readOnly && !config?.fromConstAttributeFeature && (
          <ResizeHandler
            orientation="horizontal"
            onResize={handleResizeColumn}
            initialSize={width}
            minSize={MIN_WIDTH}
          />
        )
      }
      {showAddButton && (
        <IconButton
          className="column-add-button"
          size="small"
          onClick={handleAddColumnClick}
          sx={{
            position: 'absolute',
            right: -6,
            top: '100%',
            transform: 'translateY(-50%)',
            width: 12,
            height: 12,
            opacity: 0,
            transition: 'opacity 0.2s',
            zIndex: 3,
            padding: '2px',
          }}
          color="primary"
        >
          <AddIcon />
        </IconButton>
      )}
    </TableCell>
  );
};

// Add PropTypes validation
ColumnHeader.propTypes = {
  index: PropTypes.number.isRequired,
  columnName: PropTypes.string.isRequired,
};

export default ColumnHeader;
